import { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form';

import {
  AssessmentAnswer,
  CompleteAssessmentAnswerInput,
  useProviderCompleteFormMutation,
  useProviderProgramAssessmentQuery,
  useProviderProgramAssessmentLazyQuery,
  useSendFollowUpEmailMutation,
  ProviderProgramAssessmentQuery,
} from '../../../generated/graphql';

import {
  isInitialsAssessmentQuestion,
  isSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
} from '../../lib/assessments';
import {
  mapAssessmentAnswersToMutationInput,
  ProgramAssessmentQuestion,
} from '../../lib/program-assessment';
import { getTimeStamp } from '../../lib/time';
import { getColorForSignatureUserType } from '../../lib/multiSignature';
import { signatureNameMatches } from '../../lib/string';
import { useAuth } from '../../../contexts/AuthContext';
import Spinner from '../../svgs/Spinner';
import SignatureFlagName from '../../svgs/SignatureFlagName';
import SignatureFlagRole from '../../svgs/SignatureFlagRole';

import Button from '../../components/Button';
import Avatar from '../../components/Avatar';
import Checkbox from '../../components/Checkbox';
import InputGroup from '../../components/InputGroup';
import CompletedSignature from '../../components/CompletedSignature';
import { customToast } from '../../components/ToastAlert/customToast';
import ModalDialog from '../../components/ModalDialog';
import Tooltip from '../../components/Tooltip';

import {
  CompleteProviderFormData,
  isIncompleteClientSignatureQuestion,
  isExpectedProviderFormError,
  isOnlyDraftAnswersError,
} from './helpers';
import Question from './Question';
import SignatureDisplay from './SignatureDisplay';
import { Transition } from '@headlessui/react';
import { defaultEnterTransitionProps } from '../../../v1/lib/animation';
import { useScrollToFirstError } from '../../hooks/useScrollToFirstError';
import AnimatedStatusIndicator from '../../components/SignatureStatus/AnimatedStatusIndicator';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';
import { FormSaveStatus } from '../../components/FormSaveStatus';
import { logger } from '../../../lib/logger';
import { useDraftRestoration } from '../../hooks/useDraftRestoration';
import { MULTI_FORM_FETCH_DELAY_MS } from '../../lib/constants';
import { coerceCheckboxBoolean } from '../../lib/register-options';

const CompleteProviderForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();
  const location = useLocation();
  const [showRemainingFormsModal, setShowRemainingFormsModal] = useState(false);
  const [showContinueSigningModal, setShowContinueSigningModal] =
    useState(false);

  const locationState = location.state as {
    remainingProgramAssessmentIdsNeedingSignature?: string[];
    prefilledNameAndRole?: {
      prefilledName: string;
      prefilledRole: string;
    };
    shouldNavigateBack?: boolean;
    fromFollowUpBuilder?: boolean;
    assessmentIdToFollowUpIdMap?: { [assessmentId: string]: string };
    assessmentIdToProgramIdMap?: { [assessmentId: string]: string };
    allAssessmentIdsOrdered?: string[];
    assessmentIdToPatientNameMap?: { [assessmentId: string]: string };
  };

  const programAssessmentIdsNeedingSignature =
    locationState?.remainingProgramAssessmentIdsNeedingSignature;

  const prefilledName = locationState?.prefilledNameAndRole?.prefilledName;
  const prefilledRole = locationState?.prefilledNameAndRole?.prefilledRole;

  const programAssessmentId = params.programAssessmentId || '';

  // Calculate client counter for multi-recipient flow
  const clientCounter = useMemo(() => {
    if (
      !locationState?.fromFollowUpBuilder ||
      !locationState?.assessmentIdToFollowUpIdMap ||
      !programAssessmentId
    ) {
      return null;
    }

    // Total clients = number of unique follow-up IDs
    const uniqueFollowUpIds = new Set(
      Object.values(locationState.assessmentIdToFollowUpIdMap),
    );
    const totalClients = uniqueFollowUpIds.size;

    if (totalClients <= 1) {
      return null; // Don't show counter for single client
    }

    // Get current follow-up ID
    const currentFollowUpId =
      locationState.assessmentIdToFollowUpIdMap[programAssessmentId];
    if (!currentFollowUpId) {
      return null;
    }

    // Count how many unique follow-up IDs come after the current one
    // by checking which assessment IDs in remainingProgramAssessmentIdsNeedingSignature
    // belong to different follow-up IDs
    const remainingAssessmentIds = programAssessmentIdsNeedingSignature || [];
    const remainingFollowUpIds = new Set<string>();
    remainingAssessmentIds.forEach((assessmentId) => {
      const followUpId =
        locationState.assessmentIdToFollowUpIdMap[assessmentId];
      if (followUpId && followUpId !== currentFollowUpId) {
        remainingFollowUpIds.add(followUpId);
      }
    });

    // Current client index = total - remaining unique follow-up IDs
    const currentClientIndex = totalClients - remainingFollowUpIds.size;

    return `(Client ${currentClientIndex} of ${totalClients})`;
  }, [
    locationState?.fromFollowUpBuilder,
    locationState?.assessmentIdToFollowUpIdMap,
    programAssessmentId,
    programAssessmentIdsNeedingSignature,
  ]);

  // Tooltip content logic - only show for multi-recipient flow
  const tooltipContent = useMemo(() => {
    // Only show tooltip for multi-recipient flow from FollowUpBuilder
    if (!locationState?.fromFollowUpBuilder) {
      return null;
    }

    if (
      !locationState?.allAssessmentIdsOrdered ||
      !locationState?.assessmentIdToPatientNameMap ||
      !locationState?.assessmentIdToFollowUpIdMap
    ) {
      return 'Client details';
    }

    const uniqueClients: {
      id: string;
      name: string;
      status: 'signed' | 'current' | 'pending';
    }[] = [];
    const processedFollowUpIds = new Set<string>();
    const currentFollowUpId =
      locationState.assessmentIdToFollowUpIdMap[programAssessmentId];

    locationState.allAssessmentIdsOrdered.forEach((assessmentId) => {
      const followUpId =
        locationState.assessmentIdToFollowUpIdMap?.[assessmentId];
      if (followUpId && !processedFollowUpIds.has(followUpId)) {
        processedFollowUpIds.add(followUpId);
        const name =
          locationState.assessmentIdToPatientNameMap?.[assessmentId] ||
          'Client';

        let status: 'signed' | 'current' | 'pending' = 'pending';
        // Refine status logic based on order
        // We need to know if it's already done.
        // The remaining list contains current and future.
        const isRemaining = programAssessmentIdsNeedingSignature?.some(
          (id) =>
            locationState.assessmentIdToFollowUpIdMap?.[id] === followUpId,
        );

        if (followUpId === currentFollowUpId) {
          status = 'current';
        } else if (isRemaining) {
          status = 'pending';
        } else {
          status = 'signed';
        }

        uniqueClients.push({ id: followUpId, name, status });
      }
    });

    return (
      <div className="flex flex-col gap-2 p-1">
        {uniqueClients.map((client) => (
          <div key={client.id} className="flex items-center gap-2">
            <AnimatedStatusIndicator
              isSigned={client.status === 'signed'}
              isCurrentSigner={client.status === 'current'}
              size="md"
            />
            <span className="text-caption text-neutral-125">{client.name}</span>
          </div>
        ))}
      </div>
    );
  }, [
    locationState,
    programAssessmentId,
    programAssessmentIdsNeedingSignature,
  ]);

  const navigateToNextPage = async () => {
    // Save draft before navigating away
    if (manualSave) {
      try {
        await manualSave();
      } catch (error) {
        logger.error(
          error instanceof Error
            ? error
            : new Error('Error saving draft before navigation'),
          { error },
        );
        // Continue with navigation even if save fails
      }
    }

    const shouldNavigateBack = locationState?.shouldNavigateBack ?? false;

    if (shouldNavigateBack) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const [sendFollowUpEmail] = useSendFollowUpEmailMutation();

  // Identify all forms for the same recipient (same follow-up ID)
  const currentRecipientAssessmentIds = useMemo(() => {
    if (
      !locationState?.fromFollowUpBuilder ||
      !locationState?.assessmentIdToFollowUpIdMap ||
      !programAssessmentId
    ) {
      // Single form mode - backward compatibility
      return [programAssessmentId];
    }

    const currentFollowUpId =
      locationState.assessmentIdToFollowUpIdMap[programAssessmentId];
    if (!currentFollowUpId) {
      return [programAssessmentId];
    }

    // Find all assessment IDs that map to the same follow-up ID
    return Object.entries(locationState.assessmentIdToFollowUpIdMap)
      .filter(([_, followUpId]) => followUpId === currentFollowUpId)
      .map(([assessmentId]) => assessmentId);
  }, [
    locationState?.fromFollowUpBuilder,
    locationState?.assessmentIdToFollowUpIdMap,
    programAssessmentId,
  ]);

  // State to store all program assessments for multi-form mode
  const [programAssessments, setProgramAssessments] = useState<
    Array<ProviderProgramAssessmentQuery['programAssessment']>
  >([]);
  const [loadingAssessments, setLoadingAssessments] = useState<Set<string>>(
    new Set(),
  );

  // Lazy query for fetching additional assessments
  const [getProgramAssessment] = useProviderProgramAssessmentLazyQuery();

  const isMultiFormMode =
    currentRecipientAssessmentIds.length > 1 &&
    locationState?.fromFollowUpBuilder;

  // Main query for the current assessment
  // Use errorPolicy: 'all' to allow partial data even if draftAnswers has errors
  const {
    data,
    loading,
    error,
    refetch: refetchProgramAssessment,
  } = useProviderProgramAssessmentQuery({
    variables: { programAssessmentId: programAssessmentId! },
    skip: !programAssessmentId,
    errorPolicy: 'all', // Allow partial data - draftAnswers may have errors but form should still load
    onCompleted: (data) => {
      if (!data || error) {
        logger.error('Query failed or returned no data', {
          data,
          error,
          programAssessmentId,
        });
        navigateToNextPage();
      } else if (data.programAssessment) {
        // Initialize programAssessments with the first one
        setProgramAssessments([data.programAssessment]);

        // Fetch remaining assessments for the same recipient if in multi-form mode
        // IMPORTANT: Fetch sequentially with delays to avoid Apollo race condition
        if (isMultiFormMode) {
          const remainingIds = currentRecipientAssessmentIds.filter(
            (id) => id !== programAssessmentId,
          );

          // Fetch sequentially with a small delay between each request
          // This prevents Apollo from mixing up concurrent requests
          remainingIds.forEach((assessmentId, index) => {
            setTimeout(() => {
              setLoadingAssessments((prev) => new Set(prev).add(assessmentId));
              getProgramAssessment({
                variables: { programAssessmentId: assessmentId },
              })
                .then((result) => {
                  if (result.data?.programAssessment) {
                    const next = result.data.programAssessment;
                    setProgramAssessments((prev) => {
                      if (!next?.id || prev.some((a) => a?.id === next.id)) {
                        return prev;
                      }
                      return [...prev, next];
                    });
                  }
                  setLoadingAssessments((prev) => {
                    const next = new Set(prev);
                    next.delete(assessmentId);
                    return next;
                  });
                })
                .catch((err) => {
                  logger.error(
                    err instanceof Error
                      ? err
                      : new Error('Error fetching assessment'),
                    { assessmentId, error: err },
                  );
                  setLoadingAssessments((prev) => {
                    const next = new Set(prev);
                    next.delete(assessmentId);
                    return next;
                  });
                });
            }, index * MULTI_FORM_FETCH_DELAY_MS);
          });
        }
      }
    },
  });

  // Initialize default values based on mode
  const getDefaultValues = () => {
    if (isMultiFormMode) {
      const forms: CompleteProviderFormData['forms'] = {};
      currentRecipientAssessmentIds.forEach((id) => {
        forms[id] = {
          providerSignatureName: prefilledName || '',
          providerSignatureRole: prefilledRole || '',
          signatureTermsAgreed: false,
          answers: [],
        };
      });
      return { forms };
    } else {
      return {
        providerSignatureName: prefilledName || '',
        providerSignatureRole: prefilledRole || '',
        signatureTermsAgreed: false,
        answers: [],
      };
    }
  };

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    formState,
    reset,
  } = useForm<CompleteProviderFormData>({
    defaultValues: getDefaultValues(),
  });

  // Reset form when programAssessmentId changes to prevent data from previous form persisting
  useEffect(() => {
    reset(getDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    programAssessmentId,
    isMultiFormMode,
    currentRecipientAssessmentIds.length,
    prefilledName,
    prefilledRole,
  ]);

  // Use field array for answers (single form mode)
  useFieldArray({
    control,
    name: 'answers',
    rules: { required: false },
  });

  // Watch values - support both single and multi-form modes
  const watchSignatureName = isMultiFormMode
    ? undefined
    : watch('providerSignatureName');
  const watchSignatureRole = isMultiFormMode
    ? undefined
    : watch('providerSignatureRole');
  const watchSignatureTermsAgreed = isMultiFormMode
    ? undefined
    : watch('signatureTermsAgreed');

  // Watch all form fields upfront for multi-form mode to avoid Rules of Hooks violation
  // This ensures all watch() calls happen at the component level, not conditionally inside map()
  const watchedForms = isMultiFormMode ? watch('forms') : undefined;

  // Use programAssessments in multi-form mode, otherwise use single assessment.
  // Dedupe by id so we never register two checkboxes for the same field (RHF -> ['on','on']).
  const assessmentsToDisplay = useMemo(() => {
    const raw = isMultiFormMode
      ? programAssessments
      : data?.programAssessment
      ? [data.programAssessment]
      : [];
    if (!isMultiFormMode) return raw;
    const seen = new Set<string>();
    return raw.filter((a) => {
      const id = a?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [isMultiFormMode, programAssessments, data?.programAssessment]);

  const programAssessment = data?.programAssessment;
  const programAssessmentSignatureUsers =
    programAssessment?.programAssessmentSignatureUsers;

  const currentSigner = programAssessment?.currentSigner;

  // Check if all assessments are loaded
  const allAssessmentsLoaded =
    !isMultiFormMode ||
    (programAssessments.length === currentRecipientAssessmentIds.length &&
      loadingAssessments.size === 0);

  const providerIsNotCurrentSigner =
    currentSigner &&
    authedProviderUser &&
    currentSigner?.providerUser?.id !== authedProviderUser?.id;

  const formIsReadOnly = providerIsNotCurrentSigner || !currentSigner;

  useEffect(() => {
    if (isMultiFormMode && currentRecipientAssessmentIds.length > 0) {
      // Set prefilled values for all forms in multi-form mode
      currentRecipientAssessmentIds.forEach((assessmentId) => {
        if (prefilledName) {
          setValue(
            `forms.${assessmentId}.providerSignatureName` as any,
            prefilledName,
          );
        }
        if (prefilledRole) {
          setValue(
            `forms.${assessmentId}.providerSignatureRole` as any,
            prefilledRole,
          );
        }
      });
    } else {
      // Single form mode
      if (prefilledName) {
        setValue('providerSignatureName', prefilledName);
      }
      if (prefilledRole) {
        setValue('providerSignatureRole', prefilledRole);
      }
    }
  }, [
    programAssessmentId,
    isMultiFormMode,
    currentRecipientAssessmentIds,
    prefilledName,
    prefilledRole,
    setValue,
  ]);

  useEffect(() => {
    if (providerIsNotCurrentSigner) {
      const userHasAlreadySigned = programAssessmentSignatureUsers?.some(
        (user) =>
          user.providerUser?.id === authedProviderUser?.id && user.signedAt,
      );

      if (userHasAlreadySigned) {
        customToast.success(`You have already completed this form.`);
      } else {
        customToast.warning(`It's not your turn to complete this form.`);
      }
    }
  }, [providerIsNotCurrentSigner]);

  useScrollToFirstError(formState);

  const questions = programAssessment?.assessment
    ?.questions as ProgramAssessmentQuestion[];

  // Restore draft answers and signature fields when form loads
  useDraftRestoration({
    programAssessment,
    questions,
    programAssessmentId,
    reset,
    setValue,
    isMultiFormMode,
    getDefaultValues,
  });

  const formValues = watch();

  // Build questions map for multi-form mode
  const questionsMap = useMemo(() => {
    if (!isMultiFormMode) return undefined;
    const map = new Map<string, ProgramAssessmentQuestion[]>();
    assessmentsToDisplay.forEach((assessment) => {
      if (assessment?.id && assessment?.assessment?.questions) {
        map.set(
          assessment.id,
          assessment.assessment.questions as ProgramAssessmentQuestion[],
        );
      }
    });
    return map;
  }, [isMultiFormMode, assessmentsToDisplay]);

  // Auto-save form drafts
  const { isSaving, lastSaved, saveError, manualSave } = useFormAutoSave({
    programAssessmentId,
    formData: formValues,
    questions: questions || [],
    enabled: !formIsReadOnly,
    refetchQuery: refetchProgramAssessment,
    programAssessmentIds: isMultiFormMode
      ? currentRecipientAssessmentIds
      : undefined,
    questionsMap: questionsMap,
  });

  const [providerCompleteForm] = useProviderCompleteFormMutation();

  const nowFormattedTimestamp = getTimeStamp(
    new Date().toISOString(),
    true,
    false,
  );

  const onSubmit: SubmitHandler<CompleteProviderFormData> = async (
    formData,
  ) => {
    try {
      if (isMultiFormMode && formData.forms) {
        // Multi-form mode: submit all forms together
        const submissionPromises = currentRecipientAssessmentIds.map(
          async (assessmentId) => {
            const formDataForAssessment = formData.forms![assessmentId];
            const assessment = programAssessments.find(
              (a) => a?.id === assessmentId,
            );
            if (!assessment || !formDataForAssessment) {
              throw new Error(`Missing data for assessment ${assessmentId}`);
            }

            const questions = assessment.assessment
              ?.questions as ProgramAssessmentQuestion[];
            const assessmentAnswers: CompleteAssessmentAnswerInput[] =
              mapAssessmentAnswersToMutationInput(
                questions,
                formDataForAssessment.answers,
              );

            return providerCompleteForm({
              variables: {
                input: {
                  programAssessmentId: assessmentId,
                  signatureName: formDataForAssessment.providerSignatureName,
                  signatureRole: formDataForAssessment.providerSignatureRole,
                  assessmentAnswers,
                  // Skip "Please complete {form_name}" email when coming from FollowUpBuilder
                  ...(locationState?.fromFollowUpBuilder && {
                    skipEmail: true,
                  }),
                },
              },
            });
          },
        );

        await Promise.all(submissionPromises);

        const formNames = programAssessments
          .map((a) => a?.assessment?.name)
          .filter(Boolean)
          .join(', ');
        customToast.success(`Successfully completed ${formNames}!`);

        // Send email after all forms are submitted
        if (
          locationState?.fromFollowUpBuilder &&
          locationState?.assessmentIdToFollowUpIdMap
        ) {
          const programFollowUpId =
            locationState.assessmentIdToFollowUpIdMap[programAssessmentId];
          if (programFollowUpId) {
            try {
              await sendFollowUpEmail({
                variables: { programFollowUpId },
              });
            } catch (err) {
              console.error('Error sending follow-up email:', err);
            }
          }
        }

        // Remove all completed assessment IDs from remaining list
        const remainingAfterCurrentRecipient =
          programAssessmentIdsNeedingSignature?.filter(
            (id) => !currentRecipientAssessmentIds.includes(id),
          ) || [];

        if (remainingAfterCurrentRecipient.length > 0) {
          // Auto-navigate to next client
          const nextId = remainingAfterCurrentRecipient[0];
          customToast.success('Forms completed. Moving to next client...');
          navigate(`/provider-form/${nextId}`, {
            state: {
              ...locationState,
              remainingProgramAssessmentIdsNeedingSignature:
                remainingAfterCurrentRecipient,
            },
          });
        } else {
          // All signatures complete for this batch
          if (locationState?.fromFollowUpBuilder) {
            // Check if there are ANY remaining forms (not just in this batch)
            const hasMoreForms =
              programAssessmentIdsNeedingSignature &&
              programAssessmentIdsNeedingSignature.length > 0;
            if (hasMoreForms) {
              // More forms exist - show continue signing modal
              setShowContinueSigningModal(true);
            } else {
              // Truly all done - show completion modal
              setShowRemainingFormsModal(true);
            }
          } else {
            navigateToNextPage();
          }
        }
      } else {
        // Single form mode - original logic
        const assessmentAnswers: CompleteAssessmentAnswerInput[] =
          mapAssessmentAnswersToMutationInput(
            questions,
            formData.answers || [],
          );

        await providerCompleteForm({
          variables: {
            input: {
              programAssessmentId,
              signatureName: formData.providerSignatureName || '',
              signatureRole: formData.providerSignatureRole || '',
              assessmentAnswers,
              ...(locationState?.fromFollowUpBuilder && {
                skipEmail: true,
              }),
            },
          },
        });

        customToast.success(
          `Successfully completed ${programAssessment?.assessment.name}!`,
        );

        if (
          locationState?.fromFollowUpBuilder &&
          locationState?.assessmentIdToFollowUpIdMap
        ) {
          const programFollowUpId =
            locationState.assessmentIdToFollowUpIdMap[programAssessmentId];
          if (programFollowUpId) {
            try {
              await sendFollowUpEmail({
                variables: { programFollowUpId },
              });
            } catch (err) {
              console.error('Error sending follow-up email:', err);
            }
          }
        }
        if (
          programAssessmentIdsNeedingSignature &&
          programAssessmentIdsNeedingSignature.length > 0
        ) {
          // Check if this is multi-recipient flow or single-form flow
          if (locationState?.fromFollowUpBuilder) {
            // Multi-recipient flow - show continue signing modal
            setShowContinueSigningModal(true);
          } else {
            // Single-form flow - auto-navigate to next form
            // Only pass name and role (not answers) to prefill next form
            const nextId = programAssessmentIdsNeedingSignature[0];
            window.scrollTo(0, 0);
            navigate(`/provider-form/${nextId}`, {
              state: {
                remainingProgramAssessmentIdsNeedingSignature:
                  programAssessmentIdsNeedingSignature.slice(1),
                prefilledNameAndRole: {
                  prefilledName: formData.providerSignatureName || '',
                  prefilledRole: formData.providerSignatureRole || '',
                },
              },
            });
          }
        } else {
          if (locationState?.fromFollowUpBuilder) {
            setShowRemainingFormsModal(true);
          } else {
            navigateToNextPage();
          }
        }
      }
    } catch (err) {
      console.error('Error submitting forms:', err);
      customToast.error('Failed to submit forms. Please try again.');
    }
  };

  const isLoading = loading || (isMultiFormMode && !allAssessmentsLoaded);
  const hasData = isMultiFormMode
    ? assessmentsToDisplay.length > 0
    : Boolean(programAssessment);

  // Scroll to top when programAssessmentId changes (switching between clients)
  // Also scroll when data finishes loading to ensure content is rendered
  // NOTE: This hook must be called before any early returns to comply with Rules of Hooks
  useEffect(() => {
    if (!isLoading && hasData) {
      // Use multiple attempts to ensure scroll happens after all rendering
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const timeout1 = setTimeout(
        () => window.scrollTo({ top: 0, behavior: 'smooth' }),
        100,
      );
      const timeout2 = setTimeout(
        () => window.scrollTo({ top: 0, behavior: 'smooth' }),
        300,
      );
      const timeout3 = setTimeout(() => window.scrollTo(0, 0), 500);

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    }
  }, [programAssessmentId, isLoading, hasData]);

  // Check if error is only about draftAnswers (non-critical)
  const draftAnswersOnlyError = isOnlyDraftAnswersError(error);
  // Expected auth outcomes (user not allowed to view this form) are benign and
  // must not flood Sentry — they are reported as a warning breadcrumb only.
  const expectedAuthError = isExpectedProviderFormError(error);

  if (error && !draftAnswersOnlyError) {
    if (expectedAuthError) {
      // Don't capture an exception for a benign 403/404-style authorization
      // outcome; record a breadcrumb in case a real error follows.
      logger.warn('Provider form unavailable for this user', {
        programAssessmentId,
        message: error.message,
      });
    } else {
      logger.error(new Error('GraphQL query error'), {
        programAssessmentId,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        message: error.message,
      });
    }

    return (
      <Transition show={true} {...defaultEnterTransitionProps}>
        <div className="mt-20 flex h-full w-full flex-col items-center justify-center">
          <p className="text-red-500 mb-4 text-body">
            Error loading form: {error.message}
            {error.graphQLErrors && error.graphQLErrors.length > 0 && (
              <div className="text-sm mt-2">
                {error.graphQLErrors.map((err, idx) => (
                  <div key={idx}>{err.message}</div>
                ))}
              </div>
            )}
          </p>
          <Button title="Go Back" onClick={() => navigate('/home')} />
        </div>
      </Transition>
    );
  }

  // Note: draftAnswersOnlyError is checked but we continue loading the form
  // as draftAnswers errors are non-critical

  return (
    <>
      <Transition show={isLoading} {...defaultEnterTransitionProps}>
        <div className="mt-24 flex h-full w-full flex-row items-center justify-center">
          <Spinner />
        </div>
      </Transition>
      <Transition show={hasData && !isLoading} {...defaultEnterTransitionProps}>
        <div className="flex h-[100vh-64px] text-neutral-125">
          {!formIsReadOnly && (
            <div
              className={classNames(
                'sticky top-16 h-full w-1/3 min-w-[550px] p-6 pl-20',
                'transition-all duration-150 ease-in-out',
              )}
            >
              {/* Left side: Fixed SignatureUserInput */}
              {tooltipContent ? (
                <Tooltip content={tooltipContent}>
                  <div className="mb-4 flex flex-row items-center">
                    <Avatar
                      size="medium"
                      name={programAssessment?.patientNameData?.name}
                      imageUrl={
                        programAssessment?.patientNameData?.profileImageMedia
                          ?.url
                      }
                      className="mr-2.5"
                    />
                    <div className="text-body font-medium">
                      {programAssessment?.patientNameData?.name}
                      {clientCounter && (
                        <span className="ml-2 text-neutral-200">
                          {clientCounter}
                        </span>
                      )}
                    </div>
                  </div>
                </Tooltip>
              ) : (
                <div className="mb-4 flex flex-row items-center">
                  <Avatar
                    size="medium"
                    name={programAssessment?.patientNameData?.name}
                    imageUrl={
                      programAssessment?.patientNameData?.profileImageMedia?.url
                    }
                    className="mr-2.5"
                  />
                  <div className="text-body font-medium">
                    {programAssessment?.patientNameData?.name}
                  </div>
                </div>
              )}
              {!isMultiFormMode && (
                <div className="mb-3 font-serif text-subtitle-small font-light">
                  {programAssessment?.assessment.name}
                </div>
              )}
              {isMultiFormMode && (
                <div className="mb-3 font-serif text-subtitle-small font-light">
                  {assessmentsToDisplay.length} form
                  {assessmentsToDisplay.length > 1 ? 's' : ''} to complete
                </div>
              )}
              <div className="mb-4 text-caption">
                {isMultiFormMode
                  ? "It's your turn to complete the forms. Your signature will be automatically added to all designated fields. You only need to sign once per form."
                  : "It's your turn to complete the form. Your signature will be automatically added to all designated fields. You only need to sign once."}
              </div>
              {/* Signature sections - show all forms' signatures in multi-form mode */}
              {isMultiFormMode && assessmentsToDisplay.length > 0 && (
                <div className="space-y-4">
                  {assessmentsToDisplay.map((assessment, formIndex) => {
                    const assessmentId = assessment?.id;
                    if (!assessmentId) return null;

                    const formCurrentSigner = assessment.currentSigner;
                    // Use pre-watched values instead of calling watch() inside map()
                    const formSignatureName =
                      watchedForms?.[assessmentId]?.providerSignatureName;
                    const formSignatureRole =
                      watchedForms?.[assessmentId]?.providerSignatureRole;

                    const formProviderIsNotCurrentSigner =
                      formCurrentSigner &&
                      authedProviderUser &&
                      formCurrentSigner?.providerUser?.id !==
                        authedProviderUser?.id;
                    const formIsReadOnly =
                      formProviderIsNotCurrentSigner || !formCurrentSigner;

                    if (formIsReadOnly) return null;

                    return (
                      <div
                        key={assessmentId}
                        className="flex w-full items-center justify-between rounded-xl border border-neutral-50"
                      >
                        <div
                          className={classNames(
                            'flex w-full flex-col items-start justify-start rounded-l-xl border-l-[14px] p-4',
                            getColorForSignatureUserType(
                              formCurrentSigner?.assessmentSignatureUser
                                ?.signatureUserType,
                            ).borderColor,
                          )}
                        >
                          <div className="mb-2 text-caption font-medium text-neutral-200">
                            {assessment.assessment?.name}
                          </div>
                          <SignatureDisplay
                            programAssessmentSignatureUser={formCurrentSigner}
                          />

                          {(!formSignatureName || !formSignatureRole) && (
                            <>
                              <div className="mb-0.5 mt-2 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-[#e08b00]">
                                ✍️ Signature required
                              </div>
                              <div className="mb-3 text-[11px] text-neutral-125">
                                Add your name and role here to sign
                                electronically
                              </div>
                            </>
                          )}

                          <InputGroup
                            highlighted={!formSignatureName}
                            placeholder="Name"
                            IconLeft={SignatureFlagName}
                            containerClassName="mb-3 w-[398px]"
                            className="rounded-lg pl-12"
                            {...register(
                              `forms.${assessmentId}.providerSignatureName` as any,
                              {
                                required: true,
                                validate: (value) => {
                                  const isValid =
                                    value ===
                                    formCurrentSigner?.providerUser?.name;
                                  if (!isValid) {
                                    setValue(
                                      `forms.${assessmentId}.signatureTermsAgreed` as any,
                                      false,
                                    );
                                  }
                                  return (
                                    isValid ||
                                    'Please enter your full name as displayed above'
                                  );
                                },
                              },
                            )}
                            errorMessage={
                              errors.forms?.[assessmentId]
                                ?.providerSignatureName?.message
                            }
                          />
                          <InputGroup
                            highlighted={!formSignatureRole}
                            placeholder="Role"
                            IconLeft={SignatureFlagRole}
                            containerClassName="mb-4 w-[398px]"
                            className="rounded-lg pl-12"
                            {...register(
                              `forms.${assessmentId}.providerSignatureRole` as any,
                              {
                                required: {
                                  value: true,
                                  message: 'Role is required',
                                },
                              },
                            )}
                            errorMessage={
                              errors.forms?.[assessmentId]
                                ?.providerSignatureRole?.message
                            }
                          />
                          {formSignatureName && (
                            <CompletedSignature
                              isCurrentSigner
                              programAssessmentSignatureUser={formCurrentSigner}
                              signatureName={formSignatureName}
                              signatureRole={formSignatureRole}
                              hideNameAndRole={
                                !formSignatureName || !formSignatureRole
                              }
                              hideDate={
                                !formSignatureName || !formSignatureRole
                              }
                              nowFormattedTimestamp={nowFormattedTimestamp}
                              className="mt-2 mb-3"
                            />
                          )}
                          <Checkbox
                            id={`signatureTermsAgreed-${assessmentId}`}
                            className="mt-3"
                            labelContent={
                              <div className="ml-2 text-caption">
                                I agree to the electronic signature{' '}
                                <a
                                  href="https://www.homecoming.health/terms-of-service"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-100"
                                >
                                  terms of service
                                </a>
                                .
                              </div>
                            }
                            {...register(
                              `forms.${assessmentId}.signatureTermsAgreed` as any,
                              {
                                required: true,
                                setValueAs: coerceCheckboxBoolean,
                              },
                            )}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-4 mb-6 text-caption">
                    Once you sign your part, others will be notified to fill out
                    their portion of the form
                    {assessmentsToDisplay.length > 1 ? 's' : ''}.
                  </div>
                </div>
              )}
              {!isMultiFormMode && (
                <>
                  <div className="flex w-full items-center justify-between rounded-xl border border-neutral-50">
                    <div
                      className={classNames(
                        'flex w-full flex-col items-start justify-start rounded-l-xl border-l-[14px] p-4',
                        getColorForSignatureUserType(
                          programAssessment?.currentSigner
                            ?.assessmentSignatureUser?.signatureUserType,
                        ).borderColor,
                      )}
                    >
                      <SignatureDisplay
                        programAssessmentSignatureUser={currentSigner}
                      />

                      {(!watchSignatureName || !watchSignatureRole) && (
                        <>
                          <div className="mb-0.5 mt-2 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-[#e08b00]">
                            ✍️ Signature required
                          </div>
                          <div className="mb-3 text-[11px] text-neutral-125">
                            Add your name and role here to sign electronically
                          </div>
                        </>
                      )}

                      <InputGroup
                        highlighted={!watchSignatureName}
                        placeholder="Name"
                        IconLeft={SignatureFlagName}
                        containerClassName="mb-3 w-[398px]"
                        className="rounded-lg pl-12"
                        {...register('providerSignatureName', {
                          required: true,
                          validate: (value) => {
                            const isValid = signatureNameMatches(
                              value,
                              currentSigner?.providerUser?.name,
                            );
                            if (!isValid) {
                              setValue('signatureTermsAgreed', false);
                            }
                            return (
                              isValid ||
                              'Please enter your full name as displayed above'
                            );
                          },
                        })}
                        errorMessage={errors.providerSignatureName?.message}
                      />
                      <InputGroup
                        highlighted={!watchSignatureRole}
                        placeholder="Role"
                        IconLeft={SignatureFlagRole}
                        containerClassName="mb-4 w-[398px]"
                        className="rounded-lg pl-12"
                        {...register('providerSignatureRole', {
                          required: {
                            value: true,
                            message: 'Role is required',
                          },
                        })}
                        errorMessage={errors.providerSignatureRole?.message}
                      />
                      {watchSignatureName && (
                        <CompletedSignature
                          isCurrentSigner
                          programAssessmentSignatureUser={currentSigner}
                          signatureName={watchSignatureName}
                          signatureRole={watchSignatureRole}
                          hideNameAndRole={
                            !watchSignatureName || !watchSignatureRole
                          }
                          hideDate={!watchSignatureName || !watchSignatureRole}
                          nowFormattedTimestamp={nowFormattedTimestamp}
                          className="mt-2 mb-3"
                        />
                      )}
                      <Checkbox
                        id="signatureTermsAgreed"
                        className="mt-3"
                        labelContent={
                          <div className="ml-2 text-caption">
                            I agree to the electronic signature{' '}
                            <a
                              href="https://www.homecoming.health/terms-of-service"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-100"
                            >
                              terms of service
                            </a>
                            .
                          </div>
                        }
                        {...register('signatureTermsAgreed', {
                          required: true,
                          setValueAs: coerceCheckboxBoolean,
                        })}
                      />
                    </div>
                  </div>
                  <div className="mt-4 mb-6 text-caption">
                    Once you sign your part, others will be notified to fill out
                    their portion of the form.
                  </div>
                </>
              )}
              {formState.submitCount > 0 &&
                Object.keys(formState.errors).length > 0 && (
                  <div className="mb-4 px-4 py-3 text-caption text-red-100">
                    Some questions are still incomplete, please look over the
                    form{isMultiFormMode ? 's' : ''} and complete all required
                    questions
                  </div>
                )}
              <div className="flex flex-row items-center justify-end">
                <Button
                  title="Save and complete later"
                  theme="secondary"
                  size="small"
                  className="mr-3"
                  onClick={navigateToNextPage}
                />
                <Button
                  title={
                    isMultiFormMode
                      ? 'Complete & Send'
                      : Boolean(programAssessmentIdsNeedingSignature?.length) ||
                        currentSigner?.orderIndex !== 0
                      ? 'Complete form'
                      : 'Complete & Send'
                  }
                  size="small"
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    isMultiFormMode
                      ? currentRecipientAssessmentIds.some(
                          (id) => !watchedForms?.[id]?.signatureTermsAgreed,
                        )
                      : !watchSignatureTermsAgreed
                  }
                />
              </div>
            </div>
          )}
          {/* Right side: Form content */}
          <div
            className={classNames('h-full overflow-y-auto p-6', {
              'flex w-full flex-col items-center': formIsReadOnly,
              'w-2/3': !formIsReadOnly,
            })}
          >
            <div className="flex w-full flex-col justify-start lg:w-full lg:max-w-[800px]">
              {!formIsReadOnly && (
                <div className="mb-4">
                  <FormSaveStatus
                    isSaving={isSaving}
                    lastSaved={lastSaved}
                    saveError={saveError}
                  />
                </div>
              )}
              {assessmentsToDisplay.map((assessment, formIndex) => {
                const assessmentId = assessment?.id;
                if (!assessmentId) return null;

                const formQuestions = assessment.assessment
                  ?.questions as ProgramAssessmentQuestion[];
                const formAnswers = assessment.answers as AssessmentAnswer[];
                const formSignatureUsers =
                  assessment.programAssessmentSignatureUsers;
                const formCurrentSigner = assessment.currentSigner;

                // Get form-specific watched values for multi-form mode
                // Use pre-watched values instead of calling watch() inside map()
                const formSignatureName = isMultiFormMode
                  ? watchedForms?.[assessmentId]?.providerSignatureName
                  : watchSignatureName;
                const formSignatureRole = isMultiFormMode
                  ? watchedForms?.[assessmentId]?.providerSignatureRole
                  : watchSignatureRole;
                const formSignatureTermsAgreed = isMultiFormMode
                  ? watchedForms?.[assessmentId]?.signatureTermsAgreed
                  : watchSignatureTermsAgreed;

                const formProviderIsNotCurrentSigner =
                  formCurrentSigner &&
                  authedProviderUser &&
                  formCurrentSigner?.providerUser?.id !==
                    authedProviderUser?.id;
                const formIsReadOnly =
                  formProviderIsNotCurrentSigner || !formCurrentSigner;

                return (
                  <div
                    key={assessmentId}
                    className="mb-8 rounded-lg border border-neutral-75 bg-white p-6"
                  >
                    {/* Form title */}
                    <div className="mb-4 font-serif text-subtitle-small font-light">
                      {assessment.assessment?.name}
                      {isMultiFormMode && (
                        <span className="ml-2 text-caption text-neutral-200">
                          (Form {formIndex + 1} of {assessmentsToDisplay.length}
                          )
                        </span>
                      )}
                    </div>

                    {/* Questions for this form */}
                    <div className="grid gap-y-6">
                      {formQuestions?.map((question, idx) => {
                        const isStatementQuestion =
                          isStatementAssessmentQuestion(question);

                        const isReadOnlyQuestion =
                          isIncompleteClientSignatureQuestion(
                            question,
                            formAnswers?.[idx],
                          );

                        const isInitialsQuestion =
                          isInitialsAssessmentQuestion(question);
                        const isSignatureQuestion =
                          isSignatureAssessmentQuestion(question);

                        const hasError = Boolean(
                          formState.errors.forms?.[assessmentId]?.answers?.[
                            idx
                          ] ||
                            (!isMultiFormMode &&
                              formState.errors.answers?.[idx]),
                        );

                        return (
                          <div
                            key={`${assessmentId}-${idx}-${question.question}`}
                            id={`question-${assessmentId}-${idx}`}
                            className={classNames({
                              'w-full rounded-lg border border-neutral-75 p-4':
                                !isStatementQuestion,
                              'border-red-100':
                                !isStatementQuestion && hasError,
                              'group relative transition-all duration-300 ease-in-out':
                                isReadOnlyQuestion,
                            })}
                          >
                            {assessment.assessment?.questionFrame && (
                              <div className="mb-2 text-caption text-neutral-125">
                                {assessment.assessment?.questionFrame}
                              </div>
                            )}
                            <Question
                              question={question as ProgramAssessmentQuestion}
                              answer={formAnswers?.[idx]}
                              idx={idx}
                              register={register}
                              control={control}
                              setValue={setValue}
                              programAssessmentSignatureUsers={
                                formSignatureUsers
                              }
                              currentSigner={formCurrentSigner}
                              signatureName={formSignatureName}
                              signatureRole={formSignatureRole}
                              signatureTermsAgreed={formSignatureTermsAgreed}
                              nowFormattedTimestamp={nowFormattedTimestamp}
                              formIsReadOnly={formIsReadOnly}
                              fieldPrefix={
                                isMultiFormMode
                                  ? `forms.${assessmentId}`
                                  : undefined
                              }
                            />
                            {hasError && (
                              <div className="mt-4 text-caption font-medium text-red-100">
                                {isMultiFormMode
                                  ? formState.errors.forms?.[assessmentId]
                                      ?.answers?.[idx]?.value?.message ||
                                    formState.errors.forms?.[assessmentId]
                                      ?.answers?.[idx]?.values?.message ||
                                    'Please answer above'
                                  : formState.errors.answers?.[idx]?.value
                                      ?.message ||
                                    formState.errors.answers?.[idx]?.values
                                      ?.message ||
                                    'Please answer above'}
                              </div>
                            )}
                            {isReadOnlyQuestion && (
                              <div
                                className={classNames(
                                  'absolute inset-0 flex flex-row items-center justify-center rounded-lg bg-white bg-opacity-90',
                                  'opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100',
                                )}
                              >
                                <Avatar
                                  size="medium"
                                  name={assessment.patientNameData?.name}
                                  imageUrl={
                                    assessment.patientNameData
                                      ?.profileImageMedia?.url
                                  }
                                  className="mr-2.5"
                                />
                                <div className="text-body font-medium text-neutral-125">
                                  {assessment.patientNameData?.name} will sign
                                  their {isInitialsQuestion ? 'initials' : ''}
                                  {isSignatureQuestion ? 'name' : ''} here
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Transition>
      {/* Modal for remaining unsigned forms */}
      <ModalDialog
        title="All Done!"
        isOpen={showRemainingFormsModal}
        setClosed={() => {
          setShowRemainingFormsModal(false);
          navigateToNextPage();
        }}
        width="medium"
        primaryActionTitle="Back to Dashboard"
        primaryActionOnClick={() => {
          setShowRemainingFormsModal(false);
          window.scrollTo(0, 0);
          setTimeout(() => window.scrollTo(0, 0), 100);
          navigate('/home');
        }}
      >
        <p className="text-body text-neutral-125">
          You've successfully signed all forms for all clients. Great work! 🎉
        </p>
      </ModalDialog>
      {/* Modal for continuing to sign more forms */}
      <ModalDialog
        title="Great Progress!"
        isOpen={showContinueSigningModal}
        setClosed={() => setShowContinueSigningModal(false)}
        width="medium"
        primaryActionTitle="See pending forms now"
        primaryActionOnClick={() => {
          setShowContinueSigningModal(false);
          window.scrollTo(0, 0);
          setTimeout(() => window.scrollTo(0, 0), 100);
          navigate('/home?view=unsigned-forms');
        }}
        leftFooter={
          <Button
            title="I'll do it later"
            theme="secondary"
            size="small"
            onClick={() => {
              setShowContinueSigningModal(false);
              window.scrollTo(0, 0);
              setTimeout(() => window.scrollTo(0, 0), 100);
              navigate('/home');
            }}
          />
        }
      >
        <p className="text-body text-neutral-125">
          You've signed the current forms 🎉 A few more are still pending — want
          to wrap them up?
        </p>
      </ModalDialog>
    </>
  );
};

export default CompleteProviderForm;
